import { logUploadEvent } from './loggingUtils';

// ST-5209: a video is accepted only when the file holds all the media data its
// container declares. An editor's file that was cut short (say, a partial
// download) otherwise uploads cleanly and plays frozen from where it stops.

export const INCOMPLETE_VIDEO_MESSAGE =
  "This video file is incomplete: it ends before the video does, so it wasn't added. Please re-upload the complete video file.";

const REJECTED_EVENT = 'Studio: Upload Rejected Incomplete';

export type VideoTruncation = {
  reason: 'box-past-end' | 'no-index' | 'samples-past-end' | 'element-past-end';
  fileBytes: number;
  declaredEnd?: number;
  box?: string;
};

const readBytes = (file: Blob, start: number, end: number) => {
  const slice = file.slice(start, end);
  if (typeof slice.arrayBuffer === 'function') return slice.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(slice);
  });
};

const readView = async (file: Blob, start: number, end: number) =>
  new DataView(await readBytes(file, start, Math.min(end, file.size)));

// Bounds on how much one check reads, so a pathological file can't stall the
// upload: past either, the check can't tell and accepts the file.
const MAX_INDEX_BYTES = 64 * 1024 * 1024;
const MAX_TOP_LEVEL_ELEMENTS = 10000;

// MP4 and QuickTime (ISO base media) ------------------------------------------

type Box = { type: string; start: number; headerSize: number; end: number };

/**
 * Reads the box header at `at` in `view`, whose byte 0 is at `base` in the
 * file. Size 0 runs to `parentEnd`. Null when the bytes aren't a box header:
 * too short, a type that isn't four printable ASCII characters, or a size
 * smaller than its own header.
 */
const readBox = (
  view: DataView,
  at: number,
  base: number,
  parentEnd: number
): Box | null => {
  if (view.byteLength - at < 8) return null;
  let type = '';
  for (let i = 4; i < 8; i++) {
    const code = view.getUint8(at + i);
    if (code < 0x20 || code > 0x7e) return null;
    type += String.fromCharCode(code);
  }

  const start = base + at;
  const size32 = view.getUint32(at);
  let headerSize = 8;
  let size = size32;
  if (size32 === 1) {
    if (view.byteLength - at < 16) return null;
    headerSize = 16;
    size = view.getUint32(at + 8) * 2 ** 32 + view.getUint32(at + 12);
  } else if (size32 === 0) {
    size = parentEnd - start;
  }

  return size < headerSize
    ? null
    : { type, start, headerSize, end: start + size };
};

const childBoxes = (view: DataView, start: number, end: number) => {
  const boxes: Box[] = [];
  for (let at = start; ; ) {
    const box = readBox(view, at, 0, end);
    if (!box || box.end > end) return boxes;
    boxes.push(box);
    at = box.end;
  }
};

/** A full box's entry count and where its entries start, when they fit. */
const fullBoxEntries = (
  view: DataView,
  box: Box | undefined,
  entryBytes: number
) => {
  if (!box) return null;
  const countAt = box.start + box.headerSize + 4;
  if (countAt + 4 > box.end) return null;
  const count = view.getUint32(countAt);
  const first = countAt + 4;
  return first + count * entryBytes <= box.end ? { count, first } : null;
};

/**
 * The file offset just past the last sample a sample table references, or 0
 * when the table isn't one this check reads (such as compact `stz2` sizes).
 */
const sampleTableEnd = (view: DataView, stbl: Box) => {
  const tables: Record<string, Box> = {};
  for (const box of childBoxes(view, stbl.start + stbl.headerSize, stbl.end)) {
    tables[box.type] = box;
  }

  const wide = !tables.stco && Boolean(tables.co64);
  const chunks = fullBoxEntries(view, tables.stco ?? tables.co64, wide ? 8 : 4);
  const runs = fullBoxEntries(view, tables.stsc, 12);
  // stsz: version and flags, then sample_size (0 when sizes vary),
  // sample_count and, when they vary, one size per sample.
  const stsz = tables.stsz;
  if (!chunks || !runs || !stsz) return 0;
  const sizesAt = stsz.start + stsz.headerSize + 4;
  if (sizesAt + 8 > stsz.end) return 0;
  const fixedSize = view.getUint32(sizesAt);
  const sampleCount = view.getUint32(sizesAt + 4);
  if (!fixedSize && sizesAt + 8 + sampleCount * 4 > stsz.end) return 0;

  const chunkOffset = (chunk: number) => {
    const at = chunks.first + (chunk - 1) * (wide ? 8 : 4);
    return wide
      ? view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4)
      : view.getUint32(at);
  };
  const sampleSize = (sample: number) =>
    fixedSize || view.getUint32(sizesAt + 8 + sample * 4);

  let end = 0;
  let sample = 0;
  for (let run = 0; run < runs.count; run++) {
    const at = runs.first + run * 12;
    const firstChunk = Math.max(1, view.getUint32(at));
    const samplesPerChunk = view.getUint32(at + 4);
    const lastChunk =
      run + 1 < runs.count ? view.getUint32(at + 12) - 1 : chunks.count;
    for (
      let chunk = firstChunk;
      chunk <= Math.min(lastChunk, chunks.count) && sample < sampleCount;
      chunk++
    ) {
      let bytes = 0;
      for (let i = 0; i < samplesPerChunk && sample < sampleCount; i++) {
        bytes += sampleSize(sample++);
      }
      end = Math.max(end, chunkOffset(chunk) + bytes);
    }
  }
  return end;
};

const TRACK_PATH = new Set(['trak', 'mdia', 'minf']);

// Boxes an MP4 or QuickTime file can open with. Bytes that merely look like a
// box header are not taken for a container.
const OPENING_BOXES = new Set([
  'ftyp',
  'styp',
  'moov',
  'mdat',
  'free',
  'skip',
  'wide',
  'pnot',
  'uuid',
  'sidx',
  'moof',
  'meta',
  'pdin',
]);

const indexedMediaEnd = (moov: DataView, headerSize: number) => {
  let end = 0;
  const visit = (start: number, stop: number) => {
    for (const box of childBoxes(moov, start, stop)) {
      if (box.type === 'stbl') end = Math.max(end, sampleTableEnd(moov, box));
      else if (TRACK_PATH.has(box.type)) {
        visit(box.start + box.headerSize, box.end);
      }
    }
  };
  visit(headerSize, moov.byteLength);
  return end;
};

const findIsoTruncation = async (
  file: Blob
): Promise<VideoTruncation | null> => {
  const fileBytes = file.size;
  let moov: Box | null = null;
  let boxes = 0;
  let at = 0;
  for (; fileBytes - at >= 8; boxes++) {
    if (boxes === MAX_TOP_LEVEL_ELEMENTS) return null;
    // Anything that isn't a box header (trailing padding, or not ISO media at
    // all) ends the walk without evidence either way.
    const box = readBox(await readView(file, at, at + 16), 0, at, fileBytes);
    if (!box || (!boxes && !OPENING_BOXES.has(box.type))) break;
    if (box.end > fileBytes) {
      return {
        reason: 'box-past-end',
        fileBytes,
        declaredEnd: box.end,
        box: box.type,
      };
    }
    if (box.type === 'moov') moov = box;
    at = box.end;
  }

  if (!boxes) return null;
  if (!moov) {
    // Every playable MP4 or QuickTime file has its index; one that stops
    // before the index was written has none. A walk that stopped early may
    // just not have reached it.
    return fileBytes - at < 8 ? { reason: 'no-index', fileBytes } : null;
  }
  if (moov.end - moov.start > MAX_INDEX_BYTES) return null;

  const declaredEnd = indexedMediaEnd(
    await readView(file, moov.start, moov.end),
    moov.headerSize
  );
  return declaredEnd > fileBytes
    ? { reason: 'samples-past-end', fileBytes, declaredEnd }
    : null;
};

// WebM (Matroska EBML) --------------------------------------------------------

const EBML_ID = 0x1a45dfa3;
const SEGMENT_ID = 0x18538067;

/** An EBML variable-length integer; IDs keep their length marker bit. */
const readVint = (view: DataView, at: number, keepMarker: boolean) => {
  if (at >= view.byteLength) return null;
  const first = view.getUint8(at);
  const length = Math.clz32(first) - 23;
  if (!first || at + length > view.byteLength) return null;
  const mask = 0xff >> length;
  let value = keepMarker ? first : first & mask;
  let unknown = (first & mask) === mask;
  for (let i = 1; i < length; i++) {
    const byte = view.getUint8(at + i);
    value = value * 256 + byte;
    unknown = unknown && byte === 0xff;
  }
  return { value, length, unknown };
};

/** The element at `at`, with a null end when its size is unknown. */
const readElement = async (file: Blob, at: number) => {
  const view = await readView(file, at, at + 12);
  const id = readVint(view, 0, true);
  const size = id && readVint(view, id.length, false);
  if (!id || !size) return null;
  const dataStart = at + id.length + size.length;
  return {
    id: id.value,
    dataStart,
    end: size.unknown ? null : dataStart + size.value,
  };
};

/**
 * Walks elements from `at` to the end of the file. A known size that runs
 * past it is truncation; an unknown size (a live recording) can't tell, except
 * that an unknown-size Segment's own children are walked the same way.
 */
const findEbmlTruncation = async (
  file: Blob,
  at = 0
): Promise<VideoTruncation | null> => {
  const fileBytes = file.size;
  for (let elements = 0; fileBytes - at >= 2; elements++) {
    if (elements === MAX_TOP_LEVEL_ELEMENTS) return null;
    const element = await readElement(file, at);
    if (!element) return null;
    if (element.end === null) {
      return element.id === SEGMENT_ID
        ? findEbmlTruncation(file, element.dataStart)
        : null;
    }
    if (element.end > fileBytes) {
      return {
        reason: 'element-past-end',
        fileBytes,
        declaredEnd: element.end,
      };
    }
    at = element.end;
  }
  return null;
};

// -----------------------------------------------------------------------------

/**
 * Why the file stops before the media its container declares, or null when it
 * doesn't, or isn't a container this check reads.
 */
export const findVideoTruncation = async (
  file: Blob
): Promise<VideoTruncation | null> => {
  const head = await readView(file, 0, 4);
  return head.byteLength === 4 && head.getUint32(0) === EBML_ID
    ? findEbmlTruncation(file)
    : findIsoTruncation(file);
};

export class IncompleteVideoError extends Error {
  // The editor's upload queue shows a user error's message in place of its
  // generic failure message.
  readonly isUserError = true;

  constructor(readonly truncation: VideoTruncation) {
    super(INCOMPLETE_VIDEO_MESSAGE);
    this.name = 'IncompleteVideoError';
  }
}

/**
 * The editor-facing error for a video file that ends before its media does,
 * or null when it doesn't or the check can't tell. Records each rejection.
 */
export const getIncompleteVideoError = async (
  correlationId: string,
  file: File
): Promise<IncompleteVideoError | null> => {
  let truncation: VideoTruncation | null;
  try {
    truncation = await findVideoTruncation(file);
  } catch {
    // A file the browser can't read fails the upload itself; this check only
    // rejects on evidence of truncation.
    return null;
  }
  if (!truncation) return null;

  logUploadEvent(REJECTED_EVENT, file, { correlationId, ...truncation });
  return new IncompleteVideoError(truncation);
};
